import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "../styles/galerija.css";
import Header from "./header.js";
import Footer from "./footer.js";
import { dohvatiGaleriju } from "./galerijaData.js";
import { useSEO } from "./seo.js";

const MIN_SWIPE = 50;

// Slike u prvom ekranu ne cekaju lijeno ucitavanje; ostale da.
const ODMAH_UCITAJ = 6;

const Galerija = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(null);
  /*
   * Pamti se adresa slike, a ne samo "ucitano/nije". Da se stanje resetira
   * efektom pri promjeni slike, efekt bi pregazio onLoad koji je vec stigao -
   * slika bi se ucitala, ali ostala nevidljiva.
   */
  const [ucitanaPuna, setUcitanaPuna] = useState(null);
  const [palaPuna, setPalaPuna] = useState(null);
  const [pocetakDodira, setPocetakDodira] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [greska, setGreska] = useState(null);

  useEffect(() => {
    let otkazano = false;

    dohvatiGaleriju()
      .then((ucitaneSkole) => {
        if (otkazano) return;
        setSchools(ucitaneSkole);
      })
      .catch((err) => {
        if (otkazano) return;
        console.error("Greška kod galerije:", err);
        setGreska(err.message);
      })
      .finally(() => {
        if (!otkazano) setLoading(false);
      });

    return () => {
      otkazano = true;
    };
  }, []);

  const activeSchool = schools.find((s) => s.id === schoolId);
  const currentImages = useMemo(
    () => (activeSchool ? activeSchool.images : []),
    [activeSchool]
  );

  useEffect(() => {
    setSelectedIndex(null);
  }, [schoolId]);

  // Svaka skola dobiva vlastiti naslov u trazilici, ne samo "Fotogalerija".
  useSEO({
    naslov: activeSchool
      ? `${activeSchool.name} - fotogalerija | OK Virtus`
      : "Fotogalerija - OK Virtus",
    opis: activeSchool
      ? `Fotografije s treninga Odbojkaškog kluba Virtus u dvorani ${activeSchool.name}.`
      : "Fotografije s treninga i događanja Odbojkaškog kluba Virtus po dvoranama u kojima klub trenira.",
    putanja: activeSchool ? `/galerija/${activeSchool.id}` : "/galerija",
  });

  const nextImage = useCallback(() => {
    setSelectedIndex((prev) => (prev + 1) % currentImages.length);
  }, [currentImages.length]);

  const prevImage = useCallback(() => {
    setSelectedIndex(
      (prev) => (prev - 1 + currentImages.length) % currentImages.length
    );
  }, [currentImages.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "Escape") setSelectedIndex(null);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, nextImage, prevImage]);

  const handleTouchStart = (e) => setPocetakDodira(e.touches[0].clientX);

  const handleTouchEnd = (e) => {
    if (pocetakDodira === null) return;

    const pomak = pocetakDodira - e.changedTouches[0].clientX;
    if (Math.abs(pomak) > MIN_SWIPE) {
      if (pomak > 0) nextImage();
      else prevImage();
    }

    setPocetakDodira(null);
  };

  useEffect(() => {
    if (selectedIndex === null) return;

    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedIndex]);

  // Susjedne slike se dohvate unaprijed pa listanje strelicama ne ceka mrezu.
  useEffect(() => {
    if (selectedIndex === null || currentImages.length < 2) return;

    const broj = currentImages.length;
    for (const pomak of [1, -1]) {
      const susjed = currentImages[(selectedIndex + pomak + broj) % broj];
      const slika = new Image();
      slika.sizes = susjed.punaSizes;
      slika.srcset = susjed.punaSrcSet;
      slika.src = susjed.puna;
    }
  }, [selectedIndex, currentImages]);

  const prikaziSadrzaj = () => {
    if (loading) {
      return <p className="galerija-status">Učitavanje galerije...</p>;
    }

    if (greska) {
      return (
        <p className="galerija-status">
          Galerija trenutno nije dostupna. Pokušajte ponovno kasnije.
        </p>
      );
    }

    if (schools.length === 0) {
      return <p className="galerija-status">Trenutno nema objavljenih slika.</p>;
    }

    if (!schoolId) {
      return (
        <div className="school-grid">
          {schools.map((school) => (
            <Link
              to={`/galerija/${school.id}`}
              key={school.id}
              className="school-card"
            >
              <div className="school-card-image">
                <img
                  src={school.thumbnail.src}
                  srcSet={school.thumbnail.srcSet}
                  sizes={school.thumbnail.sizes}
                  alt={school.name}
                  decoding="async"
                />
                <div className="school-card-overlay">
                  <h3>{school.name}</h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      );
    }

    if (!activeSchool) {
      return (
        <>
          <p className="galerija-status">Tražena škola nije pronađena.</p>
          <div className="galerija-header">
            <button className="back-btn" onClick={() => navigate("/galerija")}>
              Povratak na popis škola
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="galerija-header">
          <h2 className="school-subtitle">{activeSchool.name}</h2>
          <button className="back-btn" onClick={() => navigate("/galerija")}>
            Povratak na popis škola
          </button>
        </div>
        {currentImages.length === 0 ? (
          <p className="galerija-status">Još nema fotografija iz ove škole.</p>
        ) : (
          <div className="image-grid">
            {currentImages.map((slika, index) => (
              <button
                key={slika.src}
                type="button"
                className="image-card"
                onClick={() => setSelectedIndex(index)}
                aria-label={`Otvori sliku ${index + 1} od ${currentImages.length}`}
              >
                <img
                  src={slika.src}
                  srcSet={slika.srcSet}
                  sizes={slika.sizes}
                  alt={`${activeSchool.name} - fotografija ${index + 1}`}
                  loading={index < ODMAH_UCITAJ ? "eager" : "lazy"}
                  decoding="async"
                />
              </button>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="galerija-container">
      <Header />
      <div className="galerija-content">{prikaziSadrzaj()}</div>

      {selectedIndex !== null && activeSchool && (
        <div
          className="modal-overlay-img"
          onClick={() => setSelectedIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Pregled fotografije"
        >
          <div
            className="modal-content-img"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <button
              className="modal-close-img"
              onClick={() => setSelectedIndex(null)}
              aria-label="Zatvori"
            >
              &times;
            </button>
            <button
              className="nav-btn-prev-img"
              onClick={prevImage}
              aria-label="Prethodna slika"
            >
              &#10094;
            </button>
            <div className="modal-image-container-img">
              {/*
                Mala slika je vec u memoriji browsera jer je bila u mrezi, pa se
                vidi odmah i drzi okvir dok se preko nje ne ucita ostra.
              */}
              <div
                className={[
                  "modal-slika-okvir",
                  ucitanaPuna === currentImages[selectedIndex].puna && "spremna",
                  palaPuna === currentImages[selectedIndex].puna && "bez-pune",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <img
                  src={currentImages[selectedIndex].src}
                  alt=""
                  aria-hidden="true"
                  className="modal-img modal-img-mala"
                />
                <img
                  key={currentImages[selectedIndex].puna}
                  ref={(el) => {
                    // Slika iz predmemorije zna biti gotova prije nego React
                    // stigne zakaciti onLoad.
                    if (el && el.complete && el.naturalWidth > 0) {
                      setUcitanaPuna(currentImages[selectedIndex].puna);
                    }
                  }}
                  src={currentImages[selectedIndex].puna}
                  srcSet={currentImages[selectedIndex].punaSrcSet}
                  sizes={currentImages[selectedIndex].punaSizes}
                  alt={`${activeSchool.name} - fotografija ${selectedIndex + 1}`}
                  className={`modal-img modal-img-puna${
                    ucitanaPuna === currentImages[selectedIndex].puna
                      ? " ucitana"
                      : ""
                  }`}
                  decoding="async"
                  onLoad={() =>
                    setUcitanaPuna(currentImages[selectedIndex].puna)
                  }
                  onError={() => setPalaPuna(currentImages[selectedIndex].puna)}
                />
              </div>
              <div className="image-counter-img">
                {selectedIndex + 1} / {currentImages.length}
              </div>
            </div>
            <button
              className="nav-btn-next-img"
              onClick={nextImage}
              aria-label="Sljedeća slika"
            >
              &#10095;
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Galerija;
